import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';

import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import { useDataProvider, getDayStart, getDayEnd } from 'src/app/common/utils';
import { ModulePermissionProps } from 'src/app/common/module/types';

import { fetchQRActivityReportList, exportQRActivityReport } from '../../../network/activityTrackingCrud';
import { QRActivityReportPaginateList, QRActivityReportListParam } from '../../../types/activity-tracking-types';
import { FRAMEWORK_OPTIONS } from '../../../constans';

type QRActivityReportListPageProps = {} & ParamsProps & ModulePermissionProps;

const initialState: QRActivityReportListParam = {
  availableFrom: getDayStart(new Date()),
  availableTo: null, //getDayEnd(moment(getDayStart(new Date())).add(3, 'months').toDate())
  attendanceAgentCode: '',
  hostAgentCode: '',
  activityName: '',
  activityType: '',
  category: '',
  framework: '',
  officeCode: '',
  checkInAt: '',
  sortBy: 'checkInAt:desc',
  page: 1,
  limit: 5,
};

const paramsInitiator = (initialParams?: Record<string, string>): QRActivityReportListParam => {
  return initialParams
    ? {
        availableFrom: initialParams.availableFrom
          ? getDayStart(new Date(initialParams.availableFrom))
          : getDayStart(new Date()),
        availableTo: initialParams.availableTo
          ? getDayEnd(new Date(initialParams.availableTo))
          : null, //getDayEnd(moment(getDayStart(new Date())).add(3, 'months').toDate())
        attendanceAgentCode: initialParams.attendanceAgentCode || '',
        hostAgentCode: initialParams.hostAgentCode || '',
        activityName: initialParams.activityName || '',
        activityType: initialParams.activityType || '',
        category: initialParams.category || '',
        framework: initialParams.framework || '',
        officeCode: initialParams.officeCode || '',
        checkInAt: '',
        sortBy: 'checkInAt:desc',
        page: 1,
        limit: 5,
      }
    : initialState;
};

const QRActivityReportListPage: FC<QRActivityReportListPageProps> = ({
  initialParams,
  onChangeQueryParams,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const Translation = (id: string) => intl.formatMessage({ id });

  const [filterState, setFilterState] = useState<QRActivityReportListParam>(paramsInitiator(initialParams));
  const [reportList, setReportList] = useState<QRActivityReportPaginateList>();
  const [exporting, setExporting] = useState(false);
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);
  const [hasSearchLoadingStarted, setHasSearchLoadingStarted] = useState(false);

  const { isLoading, refreshData } = useDataProvider<QRActivityReportPaginateList>(
    async () => {
      try {
        if (onChangeQueryParams) onChangeQueryParams(filterState);
        return await fetchQRActivityReportList(filterState, dispatch);
      } catch (err) {}
    },
    setReportList,
    false,
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportQRActivityReport(filterState, dispatch);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `QR-Activity-Report_${moment().format('YYYYMMDD')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: Translation('activityTracking.qrActivityReport.exportSuccess'),
          },
        ]),
      );
    } catch (err) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: Translation('activityTracking.notify.error'),
          },
        ]),
      );
    } finally {
      setExporting(false);
    }
  };

  const handleSearch = () => {
    setIsSearchTriggered(true);
    setHasSearchLoadingStarted(false);
    refreshData();
  };

  useEffect(() => {
    if (isSearchTriggered && isLoading) {
      setHasSearchLoadingStarted(true);
    }
  }, [isLoading, isSearchTriggered]);

  useEffect(() => {
    if (isSearchTriggered && hasSearchLoadingStarted && !isLoading) {
      if (!reportList?.items?.length) {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.WARNING,
              title: 'Warning',
              content: 'No data match with the searching criteria',
            },
          ]),
        );
      }
      setIsSearchTriggered(false);
      setHasSearchLoadingStarted(false);
    }
  }, [dispatch, hasSearchLoadingStarted, isLoading, isSearchTriggered, reportList]);

  return (
    <>
      <PruFilter
        title={Translation('activityTracking.qrActivityReport.filter')}
        itemDef={[
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'availableFrom',
            fieldTo: 'availableTo',
            initialDateFrom: filterState.availableFrom,
            initialDateTo: filterState.availableTo,
            displayName: Translation('activityTracking.qrActivityReport.checkInDate'),
            periodTime: { months: 3 },
            readOnlyFrom: true,
            readOnlyTo: true,
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'attendanceAgentCode',
            initialValue: filterState.attendanceAgentCode,
            displayName: Translation('activityTracking.qrActivityReport.attendanceAgentCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'hostAgentCode',
            initialValue: filterState.hostAgentCode,
            displayName: Translation('activityTracking.qrActivityReport.hostAgentCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'activityName',
            initialValue: filterState.activityName,
            displayName: Translation('activityTracking.qrActivityReport.activityName'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'activityType',
            initialValue: filterState.activityType,
            displayName: Translation('activityTracking.qrActivityReport.activityType'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'category',
            initialValue: filterState.category,
            displayName: Translation('activityTracking.qrActivityReport.category'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'framework',
            initialValue: filterState.framework,
            displayName: Translation('activityTracking.qrActivityReport.framework'),
            choices: FRAMEWORK_OPTIONS,
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'officeCode',
            initialValue: filterState.officeCode,
            displayName: Translation('activityTracking.qrActivityReport.officeCode'),
          },
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            availableFrom: data.availableFrom,
            availableTo: data.availableTo,
            attendanceAgentCode: data.attendanceAgentCode,
            hostAgentCode: data.hostAgentCode,
            activityName: data.activityName,
            activityType: data.activityType,
            category: data.category,
            framework: data.framework,
            officeCode: data.officeCode,
          })
        }
        fetchData={handleSearch}
      />

      <PruTable
        disableBulkSelect
        dataSource={reportList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        title={Translation('activityTracking.qrActivityReport.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('app.button.export'),
            onClick: handleExport,
            disabled: exporting,
          },
        ]}
        operationDef={[]}
        columnDef={[
          {
            keyIndex: 'activityName',
            displayName: Translation('activityTracking.qrActivityReport.activityName'),
            renderData: (row) => row.activityName || '-',
          },
          {
            keyIndex: 'activityType',
            displayName: Translation('activityTracking.qrActivityReport.activityType'),
            renderData: (row) => row.activityType || '-',
          },
          {
            keyIndex: 'category',
            displayName: Translation('activityTracking.qrActivityReport.category'),
            renderData: (row) => row.category || '-',
          },
          {
            keyIndex: 'framework',
            displayName: Translation('activityTracking.qrActivityReport.framework'),
            renderData: (row) => row.framework || '-',
          },
          {
            keyIndex: 'activityStartDate',
            align: 'center',
            displayName: Translation('activityTracking.qrActivityReport.activityStartDate'),
            renderData: (row) =>
              row.activityStartDate ? moment(row.activityStartDate).format('DD/MM/YYYY HH:mm') : '-',
          },
          {
            keyIndex: 'activityEndDate',
            align: 'center',
            displayName: Translation('activityTracking.qrActivityReport.activityEndDate'),
            renderData: (row) =>
              row.activityEndDate ? moment(row.activityEndDate).format('DD/MM/YYYY HH:mm') : '-',
          },
          {
            keyIndex: 'hostAgentCode',
            displayName: Translation('activityTracking.qrActivityReport.hostAgentCode'),
            renderData: (row) => row.hostAgentCode || '-',
          },
          {
            keyIndex: 'hostOfficeCode',
            displayName: Translation('activityTracking.qrActivityReport.hostOfficeCode'),
            renderData: (row) => row.hostOfficeCode || '-',
          },
          {
            keyIndex: 'attendanceAgentCode',
            displayName: Translation('activityTracking.qrActivityReport.attendanceAgentCode'),
            renderData: (row) => row.attendanceAgentCode || '-',
          },
          {
            keyIndex: 'checkInAt',
            align: 'center',
            displayName: Translation('activityTracking.qrActivityReport.checkInDate'),
            renderData: (row) =>
              row.checkInAt ? moment(row.checkInAt).format('DD/MM/YYYY HH:mm') : '-',
          },
          {
            keyIndex: 'activityCreatedDate',
            align: 'center',
            displayName: Translation('activityTracking.qrActivityReport.activityCreatedDate'),
            renderData: (row) =>
              row.activityCreatedDate ? moment(row.activityCreatedDate).format('DD/MM/YYYY HH:mm') : '-',
          },
        ]}
        totalPages={reportList?.totalPages}
        totalRecords={reportList?.totalRecords}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({ ...filterState, page, limit: rowsPerPage });
          refreshData();
        }}
      />
    </>
  );
};

export default QRActivityReportListPage;
